/**
 * <ComingSoonBadge /> — Session 3a.5 FOLLOWUP §4.4 primitive.
 *
 * Renders a non-clickable gray pill stating that a feature ships in a
 * later release. Replaces placeholder stubs (window.alert / "Coming
 * in next PR" buttons) per Commandment 73 — no misleading affordances.
 *
 * Usage:
 *   <ComingSoonBadge expectedRelease="Q3 2026">
 *     Bulk Encashment
 *   </ComingSoonBadge>
 *
 * The badge is:
 *   - Not a button (no onClick, no tabIndex)
 *   - Visually clearly different from a CTA (gray / muted vs brand-gradient)
 *   - Carries the expected-release subtitle so HR knows when to expect it
 *   - Accessible — aria-label combines the label + release info
 */

import { Clock } from "lucide-react";
import type { ReactNode } from "react";

export interface ComingSoonBadgeProps {
  /** What the badge is gating (e.g., "Bulk Encashment"). */
  children: ReactNode;
  /** Human-readable release window — e.g. "Q3 2026" or "M3X.GOV". */
  expectedRelease: string;
  /** Optional feature key — useful for telemetry / future enablement. */
  feature?: string;
  /** Optional className tail for layout context. */
  className?: string;
}

export function ComingSoonBadge({
  children,
  expectedRelease,
  feature,
  className = "",
}: ComingSoonBadgeProps) {
  const label =
    typeof children === "string"
      ? `${children} — coming ${expectedRelease}`
      : `Coming ${expectedRelease}`;
  return (
    <span
      data-feature={feature}
      aria-label={label}
      className={`inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-surface-sunken px-2.5 py-1 text-xs font-medium text-text-tertiary ${className}`}
    >
      <Clock className="h-3 w-3" aria-hidden="true" />
      <span>{children}</span>
      <span className="text-[10px] uppercase tracking-wide text-text-tertiary/80">
        · {expectedRelease}
      </span>
    </span>
  );
}

export default ComingSoonBadge;
